import styled from '@emotion/styled';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import {
  TypeMicrocopySetSkeleton,
  TypeWebinars,
  TypeWebinarsSkeleton,
} from '../../../@types/generated';
import ListItemCard from '../../common-components/component-library/ListItemCard';
import SectionTitle from '../../common-components/component-library/SectionTitle';
import PrimaryButton from '../../common-components/component-library/buttons/PrimaryButton';
import FeatureHero from '../../common-components/component-library/heroes/FeatureHero';
import Container from '../../common-components/component-library/layout/Container';
import { getEntries } from '../../utils/api/contentful';
import { formatMicrocopy } from '../../utils/contentfulMicrocopy';
import { LOCALES } from '../../utils/locale';
import { spacing } from '../../utils/styleguide';
import withAppProps from '../../utils/withAppProps';
import { getLinkHref } from '../../common-components/contentful-elements/util/linkHref';

const WebinarListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]}px;
  margin-bottom: ${spacing[4]}px;
  margin-top: ${spacing[6]}px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing[10]}px;
`;

function WebinarsLandingPage({ webinars, currentPage, hasNextPage, microcopy }: Props) {
  const { replace } = useRouter();
  const pathname = usePathname();

  function handleLoadMore() {
    if (hasNextPage) {
      const params = new URLSearchParams();
      params.set('page', (currentPage + 1).toString());
      replace(`${pathname}?${params.toString()}`);
    }
  }

  const [latestWebinar, ...restWebinars] = webinars ?? [];

  if (!latestWebinar) return null;

  const now = new Date();
  const latestWebinarDate = new Date(latestWebinar.date);
  const isUpcoming = latestWebinarDate > now;

  const upcomingWebinars = restWebinars.filter((webinar) => {
    const webinarDate = new Date(webinar.date);
    return webinarDate > now;
  });
  const pastWebinars = restWebinars.filter((webinar) => {
    const webinarDate = new Date(webinar.date);
    return webinarDate <= now;
  });

  return (
    <>
      <FeatureHero
        sectionTitle={isUpcoming ? microcopy.upcomingWebinar : microcopy.recordedWebinar}
        paragraph={latestWebinar.title}
        bodyText={latestWebinar.description}
        imageUrl={latestWebinar.image?.url}
        primaryButtonLabel={microcopy.primaryCTALabel}
        primaryButtonHref={`/webinars/${latestWebinar.slug}`}
        secondaryButtonLabel={
          isUpcoming ? microcopy.secondaryCTALabelUpcoming : microcopy.secondaryCTALabelLatest
        }
        secondaryButtonHref={isUpcoming ? latestWebinar.externalLink : microcopy.secondaryCTALink}
      />
      <Container>
        {upcomingWebinars.length > 0 && (
          <>
            <SectionTitle title={microcopy.upcomingWebinars} />
            <WebinarListWrapper style={{ paddingBottom: `${spacing[6]}px` }}>
              {upcomingWebinars?.map((webinar, index) => (
                <ListItemCard
                  key={index}
                  item={{
                    ...webinar,
                    publishedAt: webinar.date,
                    externalLink: undefined,
                    type: 'Webinar',
                  }}
                />
              ))}
            </WebinarListWrapper>
          </>
        )}

        {pastWebinars.length > 0 && (
          <>
            <SectionTitle title={microcopy.recordedWebinars} />
            <WebinarListWrapper>
              {pastWebinars?.map((webinar, index) => (
                <ListItemCard
                  key={index}
                  item={{
                    ...webinar,
                    publishedAt: webinar.date,
                    externalLink: undefined,
                    type: 'Webinar',
                  }}
                />
              ))}
            </WebinarListWrapper>
          </>
        )}

        <ButtonWrapper>
          {hasNextPage && (
            <PrimaryButton label={microcopy.loadMoreLabel} onClick={handleLoadMore} />
          )}
        </ButtonWrapper>
      </Container>
    </>
  );
}

type Webinar = {
  title?: string;
  description?: string;
  image?: {
    url: string;
    alt: string;
    width: number;
    height: number;
  };
  slug: string;
  profiles: string;
  date: string;
  duration?: number;
  url: string;
  externalLink?: string;
};

type Props = {
  webinars?: Webinar[];
  currentPage: number;
  hasNextPage: boolean;
  microcopy: Record<string, string>;
};

export const getServerSideProps = withAppProps<Props>(async ({ locale, query }) => {
  const currentPage = query?.page ? Number(query.page) : 1;
  const pageSize = 7;
  const webinarsToFetch = currentPage * pageSize;

  const webinarsQuery = {
    content_type: 'webinars',
    order: '-fields.date',
    limit: webinarsToFetch,
  } as const;

  const [{ items: webinars, total }, microCopySet] = await Promise.all([
    getEntries<TypeWebinarsSkeleton>(webinarsQuery),
    getEntries<TypeMicrocopySetSkeleton>({
      content_type: 'microcopySet',
      locale: LOCALES[locale ?? 'en'],
    }),
  ]);

  const { webinarsPage } = formatMicrocopy(microCopySet);

  const hasNextPage = webinars.length < total;

  return {
    props: {
      webinars: webinars.map((webinar) => ({
        title: webinar.fields.title,
        description: webinar.fields.preamble,
        date: webinar.fields.date,
        duration: webinar.fields.duration,
        url: `/webinars/${webinar.fields.slug}`,
        externalLink: getLinkHref(webinar.fields.signUpLink),
        image: getWebinarImage(webinar),
        profiles: webinar.fields.speakersList
          ? webinar.fields.speakersList
              .filter((profile) => !!profile)
              .map((profile) => profile.fields.name)
              .join(', ')
          : '',
        slug: webinar.fields.slug,
      })),
      currentPage,
      hasNextPage,
      microcopy: webinarsPage,
    },
    pageAppProps: {
      metaHeader: {
        canonicalLink: 'https://www.milkywire.com/webinars',
        seoDescription: 'View our webinars on nature, climate and planet health.',
        seoTitle: 'Webinars | Milkywire',
        socialDescription: 'View our webinars on nature, climate and planet health.',
        socialImageHeight: 0,
        socialImageUrl: '',
        socialImageWidth: 0,
        socialTitle: 'Webinars | Milkywire',
      },
    },
  };
});

WebinarsLandingPage.layoutProps = {
  initiallyTransparentHeader: true,
  floatingHeader: true,
};

export default WebinarsLandingPage;

export function getWebinarImage(webinar: TypeWebinars) {
  let image;
  if (webinar.fields.image?.fields.imageLandscape) {
    image = {
      url: webinar.fields.image?.fields.imageLandscape.fields.file?.url ?? '',
      alt:
        webinar.fields.image?.fields.imageLandscape.fields.file?.fileName ??
        webinar.fields.title ??
        '',
      width: webinar.fields.image?.fields.imageLandscape.fields.file?.details.image?.width ?? 0,
      height: webinar.fields.image?.fields.imageLandscape.fields.file?.details.image?.height ?? 0,
    };
  } else if (webinar.fields.image?.fields.imagePortrait) {
    image = {
      url: webinar.fields.image?.fields.imagePortrait.fields.file?.url ?? '',
      alt:
        webinar.fields.image?.fields.imagePortrait.fields.file?.fileName ??
        webinar.fields.title ??
        '',
      width: webinar.fields.image?.fields.imagePortrait.fields.file?.details.image?.width ?? 0,
      height: webinar.fields.image?.fields.imagePortrait.fields.file?.details.image?.height ?? 0,
    };
  }
  return image;
}
